import { useCallback } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useMemberDirectorySettings } from "@circle-react/components/MemberDirectory/useMemberDirectorySettings";
import type { BaseFilter } from "@circle-react-shared/MemberFilters/types";
import { useMemberFilterUrl } from "@circle-react-shared/MemberFilters/useMemberFilterUrlV2";
import { STATUS_OPTIONS_MAP } from "@circle-react-shared/MembersList/constants";

const selectedTabIndexToStatusMap = new Map<number, string>([
  [0, STATUS_OPTIONS_MAP.ALL],
  [1, STATUS_OPTIONS_MAP.ALL],
  [2, STATUS_OPTIONS_MAP.ACTIVE],
  [3, STATUS_OPTIONS_MAP.INACTIVE],
  [4, STATUS_OPTIONS_MAP.ACTIVE],
  [5, STATUS_OPTIONS_MAP.ACTIVE],
]);

interface FilterParamsReturn {
  [key: string]: any;
  filters: BaseFilter[];
  onSort: (sortingState: Array<{ id: string; desc?: boolean }>) => void;
  order: Record<string, "asc" | "desc">;
  query?: string;
  scope: {
    status: string;
    visible_in_member_directory?: boolean;
    exclude_empty_profiles?: boolean;
  };
}

export const useFilterParams = (
  selectedTabIndex: number,
): FilterParamsReturn => {
  const history = useHistory();
  const location = useLocation();
  const memberDirectorySettings = useMemberDirectorySettings();

  const getDefaultOrder = useCallback(() => ({ created_at: "desc" }), []);

  const {
    queryParams: { order, filters, ...rest },
  } = useMemberFilterUrl();

  const handleChangeOrder = ([sortingState]: {
    id: string;
    // eslint-disable-next-line sonarjs/no-redundant-optional -- Disabled for plugin version update. Fix when possible
    desc?: boolean | undefined;
  }[]) => {
    const searchParams = new URLSearchParams(location.search);
    if (order) {
      Object.keys(order).forEach(key => {
        searchParams.delete(`order[${key}]`);
      });
    }
    if (sortingState) {
      searchParams.append(
        `order[${sortingState.id}]`,
        sortingState.desc ? "desc" : "asc",
      );
    }
    history.push({
      search: searchParams.toString(),
    });
  };

  return {
    ...rest,
    onSort: handleChangeOrder,
    order: order ?? getDefaultOrder(),
    filters: filters ?? [],
    scope: {
      ...memberDirectorySettings.scope,
      status:
        selectedTabIndexToStatusMap.get(selectedTabIndex) ||
        STATUS_OPTIONS_MAP.ALL,
    },
  };
};
